import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NumberInput } from '@/components/ui/number-input';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, AlertCircle, X, Trophy } from 'lucide-react';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Cayman', 'Europe/London',
  'Europe/Paris', 'Asia/Tokyo', 'Asia/Singapore', 'Australia/Sydney', 'UTC'
];

// Preset point values per sport — must mirror
// backend/sport_definitions.py :: default_point_values so the wizard preview
// matches what the server bakes in on create.
const SPORT_POINT_VALUE_DEFAULTS = {
  basketball: { win: 2, loss: 1, draw: 0, forfeit: 0 },
  soccer:     { win: 3, loss: 0, draw: 1, forfeit: 0 },
  volleyball: { win: 1, loss: 0, draw: 0, forfeit: 0 },
  netball:    { win: 2, loss: 0, draw: 1, forfeit: 0 },
  pickleball: { win: 2, loss: 0, draw: 0, forfeit: 0 },
  flag_football: { win: 2, loss: 0, draw: 1, forfeit: 0 },
  softball:   { win: 2, loss: 0, draw: 1, forfeit: 0 },
  baseball:   { win: 2, loss: 0, draw: 1, forfeit: 0 },
  cricket:    { win: 2, loss: 0, draw: 1, forfeit: 0 },
  generic:    { win: 1, loss: 0, draw: 0, forfeit: 0 },
};

export function EventInfoStep({ eventData, setEventData, errors }) {
  const [dateOpen, setDateOpen] = useState(false);

  const applySportPreset = (sportKey) => {
    setEventData(prev => ({
      ...prev,
      sport: sportKey,
      // Auto-refresh point values whenever the sport changes so organizers
      // don't have to reconfigure them in the standings tab later.
      point_values: SPORT_POINT_VALUE_DEFAULTS[sportKey] || SPORT_POINT_VALUE_DEFAULTS.generic,
      advanced_stats: sportKey === 'flag_football' ? !!prev.advanced_stats : false,
    }));
  };

  const currentPreset = SPORT_POINT_VALUE_DEFAULTS[eventData.sport] || SPORT_POINT_VALUE_DEFAULTS.basketball;

  return (
    <div className="space-y-6 wizard-step">
      <div className="form-field">
        <Label htmlFor="event-name" className="text-slate-700 font-medium">Event Name *</Label>
        <Input id="event-name" data-testid="event-name-input" value={eventData.name}
          onChange={(e) => setEventData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Summer Basketball League 2024"
          className={`mt-2 h-12 ${errors.name ? 'border-red-500' : ''}`} />
        {errors.name && <p className="text-sm text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.name}</p>}
      </div>

      <div className="form-field">
        <Label className="text-slate-700 font-medium flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#00247D]" /> Sport
        </Label>
        <p className="text-xs text-slate-500 mt-1 mb-2">
          Basketball unlocks the live stat tracker. Soccer, Volleyball, Netball, Pickleball, Flag Football, Softball, Baseball & Cricket use per-game box-score entry.
          Generic tracks only the final score.
        </p>
        <Select
          value={eventData.sport || 'basketball'}
          onValueChange={applySportPreset}
        >
          <SelectTrigger data-testid="sport-select" className="mt-1 h-12"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="basketball" data-testid="sport-basketball">Basketball</SelectItem>
            <SelectItem value="soccer" data-testid="sport-soccer">Soccer</SelectItem>
            <SelectItem value="volleyball" data-testid="sport-volleyball">Volleyball</SelectItem>
            <SelectItem value="netball" data-testid="sport-netball">Netball</SelectItem>
            <SelectItem value="pickleball" data-testid="sport-pickleball">Pickleball</SelectItem>
            <SelectItem value="flag_football" data-testid="sport-flag_football">Flag Football</SelectItem>
            <SelectItem value="softball" data-testid="sport-softball">Softball</SelectItem>
            <SelectItem value="baseball" data-testid="sport-baseball">Baseball</SelectItem>
            <SelectItem value="cricket" data-testid="sport-cricket">Cricket</SelectItem>
            <SelectItem value="generic" data-testid="sport-generic">Generic (Score Only)</SelectItem>
          </SelectContent>
        </Select>
        <div className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="sport-preset-preview">
          <span className="font-semibold text-slate-700">Auto-applied for standings:</span>{' '}
          <span className="font-mono text-slate-600">
            Win {currentPreset.win} · Loss {currentPreset.loss} · Draw {currentPreset.draw} · Forfeit {currentPreset.forfeit}
          </span>
          <span className="ml-1 text-slate-400">(editable later in Standings)</span>
        </div>
        {eventData.sport === 'flag_football' && (
          <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="advanced-stats-option">
            <Checkbox
              checked={!!eventData.advanced_stats}
              onCheckedChange={(v) => setEventData(prev => ({ ...prev, advanced_stats: !!v }))}
              className="mt-0.5"
              data-testid="advanced-stats-checkbox"
            />
            <span>
              Also track yards &amp; passing TDs <span className="text-slate-400">(optional)</span>
              <span className="block text-xs text-slate-500">Touchdowns, extra points, catches, flag pulls, interceptions and sacks are always tracked. You can change this later in any game's Box Score.</span>
            </span>
          </label>
        )}
      </div>

      <div className="form-field stagger-1">
        <Label className="text-slate-700 font-medium">Start Date *</Label>
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="start-date-btn"
              className={`w-full mt-2 h-12 justify-start text-left font-normal ${!eventData.start_date && 'text-slate-500'}`}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {eventData.start_date ? format(eventData.start_date, 'PPP') : 'Pick a date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={eventData.start_date}
              onSelect={(date) => { setEventData(prev => ({ ...prev, start_date: date })); setDateOpen(false); }} initialFocus />
          </PopoverContent>
        </Popover>
      </div>

      <div className="form-field stagger-2">
        <Label className="text-slate-700 font-medium">Timezone</Label>
        <Select value={eventData.timezone} onValueChange={(value) => setEventData(prev => ({ ...prev, timezone: value }))}>
          <SelectTrigger data-testid="timezone-select" className="mt-2 h-12"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-field stagger-3">
          <Label htmlFor="venue" className="text-slate-700 font-medium">Venue(s)</Label>
          <Input id="venue" data-testid="venue-input" value={eventData.venue}
            onChange={(e) => setEventData(prev => ({ ...prev, venue: e.target.value }))}
            placeholder="e.g., Main Stadium, Community Center, Field 3" className="mt-2 h-12" />
          <p className="text-xs text-slate-500 mt-1">Separate multiple venues with commas</p>
        </div>
        <div className="form-field stagger-4">
          <Label htmlFor="num-courts" className="text-slate-700 font-medium">Number of Courts/Fields per Game Day</Label>
          <NumberInput data-testid="num-courts-input" min={1} max={20} value={eventData.num_courts}
            onChange={(value) => setEventData(prev => ({ ...prev, num_courts: value }))} className="mt-2" />
        </div>
      </div>

      <div className="form-field stagger-5">
        <Label className="text-slate-700 font-medium mb-3 block">Game Days *</Label>
        <p className="text-sm text-slate-500 mb-3">Select which days of the week games will be played</p>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
            { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' }, { value: 6, label: 'Sun' }
          ].map((day) => {
            const isSelected = eventData.game_days.includes(day.value);
            return (
              <button key={day.value} type="button" data-testid={`game-day-${day.value}`}
                onClick={() => {
                  setEventData(prev => {
                    const newDays = isSelected
                      ? prev.game_days.filter(d => d !== day.value)
                      : [...prev.game_days, day.value].sort((a, b) => a - b);
                    return { ...prev, game_days: newDays, rules: { ...prev.rules, game_days: newDays } };
                  });
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isSelected ? 'bg-[#00247D] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>{day.label}</button>
            );
          })}
        </div>
        {eventData.game_days.length === 0 && (
          <p className="text-sm text-red-500 mt-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" />Select at least one game day</p>
        )}
      </div>

      <div className="form-field stagger-6">
        <Label htmlFor="blackout-dates" className="text-slate-700 font-medium">Blackout Dates (Optional)</Label>
        <p className="text-sm text-slate-500 mt-1 mb-2">Select dates to avoid from the calendar</p>
        <div className="flex items-start gap-2">
          <Input id="blackout-dates" data-testid="blackout-dates-input" value={eventData.blackout_dates}
            onChange={(e) => {
              const value = e.target.value;
              const datesArray = value.split(',').map(d => d.trim()).filter(d => d);
              setEventData(prev => ({ ...prev, blackout_dates: value, rules: { ...prev.rules, blackout_dates: datesArray } }));
            }}
            placeholder="Click calendar to select dates" className="mt-1 h-12 flex-1" readOnly />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="mt-1 h-12 px-3" data-testid="blackout-calendar-btn">
                <CalendarIcon className="w-5 h-5 text-[#00247D]" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="multiple"
                selected={eventData.blackout_dates
                  ? eventData.blackout_dates.split(',').map(d => d.trim()).filter(d => d).map(d => new Date(d + 'T00:00:00'))
                  : []}
                onSelect={(dates) => {
                  if (!dates || dates.length === 0) {
                    setEventData(prev => ({ ...prev, blackout_dates: '', rules: { ...prev.rules, blackout_dates: [] } }));
                    return;
                  }
                  const sorted = dates.sort((a, b) => a - b);
                  const formatted = sorted.map(d => format(d, 'yyyy-MM-dd'));
                  setEventData(prev => ({ ...prev, blackout_dates: formatted.join(', '), rules: { ...prev.rules, blackout_dates: formatted } }));
                }}
                className="rounded-md border" />
            </PopoverContent>
          </Popover>
        </div>
        {eventData.blackout_dates && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {eventData.blackout_dates.split(',').map(d => d.trim()).filter(d => d).map(date => (
              <span key={date} className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 text-xs font-medium px-2 py-1 rounded-full">
                {date}
                <button type="button" data-testid={`remove-blackout-${date}`}
                  onClick={() => {
                    const updated = eventData.blackout_dates.split(',').map(d => d.trim()).filter(d => d && d !== date);
                    setEventData(prev => ({ ...prev, blackout_dates: updated.join(', '), rules: { ...prev.rules, blackout_dates: updated } }));
                  }}
                  className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
